// components/AdBanner.js
// ---------------------------------------------------------------------------
// Full-width image banner carousel — pharmacy/quick-commerce style.
// Slides animate left/right via a CSS translateX transition on the track.
// ---------------------------------------------------------------------------

import React, { useState, useEffect, useRef, useCallback } from 'react';

const AdBanner = ({ ads = [], loading = false }) => {
  const [current, setCurrent] = useState(0);
  const [hovered, setHovered] = useState(false);
  const timerRef = useRef(null);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent(c => (c + 1) % ads.length);
    }, 4500);
  }, [ads.length]);

  useEffect(() => {
    if (ads.length > 1) startTimer();
    return () => clearInterval(timerRef.current);
  }, [ads.length, startTimer]);

  const goTo = useCallback((idx) => {
    setCurrent(idx);
    startTimer();
  }, [startTimer]);

  const prev = () => goTo((current - 1 + ads.length) % ads.length);
  const next = () => goTo((current + 1) % ads.length);

  if (!loading && ads.length === 0) return null;

  return (
    <div
      style={{ marginBottom: 20, borderRadius: 14, position: 'relative' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >

      {/* Skeleton */}
      {loading && (
        <div className="skeleton" style={{ height: 140, borderRadius: 14 }} />
      )}

      {/* Carousel */}
      {!loading && ads.length > 0 && (
        <>
          <div style={{ borderRadius: 14, overflow: 'hidden' }}>
            <div style={{
              display: 'flex',
              transform: `translateX(-${current * 100}%)`,
              transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
              willChange: 'transform',
            }}>
              {ads.map((ad, i) => (
                <div key={ad.id} style={{
                  minWidth: '100%',
                  opacity: i === current ? 1 : 0,
                  transition: 'opacity 0.8s ease',
                }}>
                  <BannerSlide ad={ad} />
                </div>
              ))}
            </div>
          </div>

          {/* Prev / Next */}
          {ads.length > 1 && (
            <>
              <NavBtn direction="left" onClick={prev} hovered={hovered} />
              <NavBtn direction="right" onClick={next} hovered={hovered} />

              {/* Dot indicators */}
              <div style={{
                position: 'absolute', bottom: 10, left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex', gap: 5, zIndex: 3,
              }}>
                {ads.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goTo(i)}
                    style={{
                      width: i === current ? 18 : 6,
                      height: 6, borderRadius: 9999, border: 'none',
                      background: i === current ? '#fff' : 'rgba(255,255,255,0.45)',
                      cursor: 'pointer', padding: 0,
                      transition: 'width .3s ease, background .3s ease',
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

// ─── Single slide ─────────────────────────────────────────────────────────────
const BannerSlide = ({ ad }) => {
  const handleClick = () => {
    if (!ad.ctaUrl) return;
    if (ad.ctaUrl.startsWith('http')) {
      window.open(ad.ctaUrl, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = ad.ctaUrl;
    }
  };

  const bgStyle = ad.imageUrl
    ? { backgroundImage: `url(${ad.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: ad.backgroundColor || 'linear-gradient(135deg, #1e1b4b, #4f46e5)' };

  const textColor = ad.textColor || '#ffffff';
  const accentColor = ad.accentColor || '#ffffff';

  return (
    <div
      onClick={ad.ctaUrl ? handleClick : undefined}
      style={{
        ...bgStyle,
        position: 'relative',
        borderRadius: 14,
        minHeight: 140,
        display: 'flex',
        alignItems: 'center',
        cursor: ad.ctaUrl ? 'pointer' : 'default',
        overflow: 'hidden',
        userSelect: 'none',
      }}
    >
      {/* Dark overlay for readability over photos */}
      <div style={{
        position: 'absolute', inset: 0,
        background: ad.imageUrl
          ? 'linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 60%, transparent 100%)'
          : 'none',
        borderRadius: 14,
      }} />

      {/* Text block */}
      <div style={{ position: 'relative', zIndex: 1, padding: 'clamp(16px, 3vw, 28px)', maxWidth: '60%' }}>
        {ad.tag && (
          <div style={{
            display: 'inline-block',
            background: accentColor, color: ad.tagTextColor || '#1e1b4b',
            fontSize: 10, fontWeight: 800, letterSpacing: '0.08em',
            textTransform: 'uppercase', padding: '3px 9px', borderRadius: 4,
            fontFamily: 'DM Sans, sans-serif', marginBottom: 8,
          }}>
            {ad.tag}
          </div>
        )}

        <div style={{
          fontSize: 'clamp(17px, 2.5vw, 24px)', fontWeight: 800,
          color: textColor, fontFamily: 'Sora, sans-serif',
          lineHeight: 1.2, marginBottom: 6, whiteSpace: 'pre-line',
        }}>
          {ad.title}
        </div>

        {ad.subtitle && (
          <div style={{
            fontSize: 'clamp(12px, 1.5vw, 14px)', color: textColor,
            opacity: 0.85, fontFamily: 'DM Sans, sans-serif',
            marginBottom: 14, lineHeight: 1.4,
          }}>
            {ad.subtitle}
          </div>
        )}

        {ad.ctaLabel && (
          <button
            onClick={(e) => { e.stopPropagation(); handleClick(); }}
            style={{
              background: accentColor, color: ad.ctaBtnTextColor || '#1e1b4b',
              border: 'none', borderRadius: 8, padding: '9px 18px',
              fontSize: 13, fontWeight: 700, fontFamily: 'DM Sans, sans-serif',
              cursor: 'pointer', letterSpacing: '0.02em', transition: 'opacity .15s',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            {ad.ctaLabel}
          </button>
        )}
      </div>

      {/* "Ad" pill */}
      <span style={{
        position: 'absolute', top: 8, right: 10, zIndex: 2,
        fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
        color: 'rgba(255,255,255,0.7)', background: 'rgba(0,0,0,0.25)',
        padding: '2px 6px', borderRadius: 9999, fontFamily: 'DM Sans, sans-serif',
      }}>
        AD
      </span>
    </div>
  );
};

// ─── Prev / Next button ───────────────────────────────────────────────────────
const NavBtn = ({ direction, onClick, hovered }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    style={{
      position: 'absolute',
      top: '50%', transform: 'translateY(-50%)',
      [direction === 'left' ? 'left' : 'right']: 10,
      zIndex: 3,
      width: 30, height: 30, borderRadius: '50%',
      background: 'rgba(0,0,0,0.30)', border: 'none', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontSize: 14, fontWeight: 700,
      backdropFilter: 'blur(4px)',
      opacity: hovered ? 1 : 0,
      transition: 'opacity .2s ease, background .15s ease',
    }}
    onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.55)'}
    onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.30)'}
  >
    {direction === 'left' ? '‹' : '›'}
  </button>
);

export default AdBanner;